import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'

import { isKey } from '../../utils'
import { useTheme } from '../../theme'

import { StyledText } from '../styled-text'
import { Flex, IFlexProps } from '../flex'
import { Radio } from '../radio'
import { Checkbox } from '../checkbox'
import { Truncated } from '../truncated'
import { ExpandButton } from '../expand-button'
import { AnimateChildHeight } from '../animate-child-height'

import getStyles from './styles'

const variants = ['normal', 'negative', 'primary']

const getIcon = (icon: 'radio' | 'checkbox' | React.ReactNode, expanded: boolean) =>
  icon ? (
    icon === 'radio' ? (
      <Radio block checked={expanded} flush />
    ) : icon === 'checkbox' ? (
      <Checkbox block checked={expanded} />
    ) : (
      icon
    )
  ) : null

const getChevronVariant = (variant?: 'normal' | 'negative' | 'primary') => (variant === 'primary' ? 'link' : 'clear')

const hasSummary = ({ summary, summaryDetail }: { summary: React.ReactNode; summaryDetail: React.ReactNode }) =>
  Boolean(summary || summaryDetail)

interface IExpandableOptionProps {
  children?: React.ReactNode
  contentStyle?: Rule
  description?: React.ReactNode
  displayExpandableIcon?: boolean
  expandable?: boolean
  expanded?: boolean
  flush?: boolean
  heading?: React.ReactNode
  icon?: 'radio' | 'checkbox' | React.ReactNode
  large?: boolean
  expandButtonOutline?: boolean
  onSelect?: () => void
  style?: Rule
  summary?: React.ReactNode
  summaryDetail?: React.ReactNode
  summarySettings?: Rule
  title?: React.ReactNode
  titleSettings?: IFlexProps
  variant?: 'normal' | 'negative' | 'primary'
}

/**
 * @type {React.StatelessComponent}
 */
export const ExpandableOption = ({
  onSelect,
  expanded = false,
  expandable,
  displayExpandableIcon,
  flush,
  contentStyle,
  large,
  title,
  description,
  heading,
  summary,
  summaryDetail,
  summarySettings,
  titleSettings,
  children,
  icon,
  style,
  variant,
  expandButtonOutline,
}: IExpandableOptionProps) => {
  const styles = getStyles(useTheme())
  return (
    <div
      {...css([
        styles.main.base,
        expanded || (isKey(styles.main.variants, variant) && styles.main.variants[variant]),
        expanded && isKey(styles.main.expanded.variants, variant) && styles.main.expanded.variants[variant],
        flush && styles.main.flush,
        style,
      ])}
    >
      {heading}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        onClick={expandable ? onSelect : undefined}
        row
        style={[styles.header.base, large && styles.header.large, expandable && styles.header.expandable]}
      >
        {icon && (
          <Flex justifyContent="center" style={styles.icon.base}>
            {getIcon(icon, expanded)}
          </Flex>
        )}
        <Flex
          flexGrow={1}
          row
          style={{
            width: `calc(100% - ${icon ? '40px' : '0px'} - ${
              children && expandable && displayExpandableIcon ? '50px' : '0px'
            })`,
          }}
        >
          <Flex
            justifyContent="center"
            width={hasSummary({ summary, summaryDetail }) ? '50%' : '100%'}
            {...titleSettings}
          >
            <StyledText style={{ maxWidth: '100%' }} weight="600">
              <Truncated>{title}</Truncated>
            </StyledText>
            <StyledText style={{ maxWidth: '100%' }} variant="dim">
              <Truncated>{description}</Truncated>
            </StyledText>
          </Flex>
          {hasSummary({ summary, summaryDetail }) && (
            <Flex alignItems="flex-end" justifyContent="center" width="50%" {...summarySettings}>
              <StyledText style={{ maxWidth: '100%' }}>
                <Truncated>{summary}</Truncated>
              </StyledText>
              {summaryDetail && (
                <StyledText style={{ maxWidth: '100%' }} variant="dim">
                  <Truncated>{summaryDetail}</Truncated>
                </StyledText>
              )}
            </Flex>
          )}
        </Flex>
        {children && displayExpandableIcon && expandable && (
          <Flex justifyContent="center" style={styles.chevron.base}>
            <ExpandButton
              expanded={expanded}
              flush
              outline={expandButtonOutline}
              style={{ lineHeight: 'inherit' }}
              variant={getChevronVariant(variant)}
            />
          </Flex>
        )}
      </Flex>
      <AnimateChildHeight>
        {expanded && children && (
          <div
            {...css(
              styles.content.base,
              contentStyle,
              isKey(styles.content.variants, variant) && styles.content.variants[variant]
            )}
          >
            {children}
          </div>
        )}
      </AnimateChildHeight>
    </div>
  )
}

ExpandableOption.propTypes = {
  children: PropTypes.node,
  contentStyle: PropTypes.object,
  description: PropTypes.node,
  displayExpandableIcon: PropTypes.bool,
  expandable: PropTypes.bool,
  expanded: PropTypes.bool,
  flush: PropTypes.bool,
  heading: PropTypes.node,
  icon: PropTypes.oneOfType([PropTypes.oneOf(['radio', 'checkbox']), PropTypes.node]),
  large: PropTypes.bool,
  expandButtonOutline: PropTypes.bool,
  onSelect: PropTypes.func,
  style: PropTypes.object,
  summary: PropTypes.node,
  summaryDetail: PropTypes.node,
  summarySettings: PropTypes.object,
  title: PropTypes.node,
  titleSettings: PropTypes.object,
  variant: PropTypes.oneOf(variants),
}

ExpandableOption.defaultProps = {
  variant: 'normal',
  expandable: true,
  displayExpandableIcon: true,
}
